import type { PendingAwaitingInput } from "@/api/awaiting-input";
import { resolveWorkToastQuestion } from "@/lib/work-toast-item";
import { findWorkToastKeyByAwaitingInputId, useWorkToastStore } from "@/stores/work-toast-store";
import { applyWorkToastQuestionDelivery, isWorkToastQuestionAnswered } from "./work-toast-question-delivery";

/** Spec §7: after a restart the project's live questions come back as pills.
 *  Seeds only what the frame adapter has not already produced, and resolves
 *  this project's questions that are no longer pending (a lost resolve frame).
 *  `fetchStartedAt` is taken just before the pending list was requested: a
 *  question that arrived since then can be missing from that snapshot. */
export function seedWorkToastQuestions(
  pendingInputs: PendingAwaitingInput[],
  projectId: string,
  fetchStartedAt: number,
  canApply: (executionId: string, questionId: string) => boolean = () => true,
): void {
  const now = Date.now();
  const pendingIds = new Set(pendingInputs.map((input) => input.id));
  for (const item of Object.values(useWorkToastStore.getState().items)) {
    if (item.answerDelivery && item.projectId === projectId &&
        item.stateChangedAt < fetchStartedAt && !pendingIds.has(item.answerDelivery.awaitingInputId) &&
        canApply(item.answerDelivery.executionId, item.answerDelivery.awaitingInputId)) {
      applyWorkToastQuestionDelivery({
        awaiting_input_id: item.answerDelivery.awaitingInputId,
        execution_id: item.answerDelivery.executionId,
        status: "superseded",
      }, { workToastStore: useWorkToastStore }, now);
    }
    const awaitingInputId = item.question?.awaitingInputId;
    if (item.projectId !== projectId || !awaitingInputId || pendingIds.has(awaitingInputId)) continue;
    if (!canApply(item.key, awaitingInputId)) continue;
    if (item.stateChangedAt >= fetchStartedAt) continue;
    resolveWorkToastQuestion({ workToastStore: useWorkToastStore }, awaitingInputId, now);
  }
  for (const input of pendingInputs) {
    const key = input.execution;
    if (!key || !canApply(key, input.id)) continue;
    if (input.status === "answered" && input.answer_delivery_status) {
      applyWorkToastQuestionDelivery({
        awaiting_input_id: input.id, execution_id: input.execution,
        project_id: projectId, conversation_id: input.conversation_id,
        task_id: input.task_id, native_subagent_id: input.native_subagent_id,
        status: input.answer_delivery_status, error: input.answer_delivery_error,
      }, { workToastStore: useWorkToastStore }, now);
      continue;
    }
    if (input.status !== "pending") continue;
    if (isWorkToastQuestionAnswered(input.id)) continue;
    const store = useWorkToastStore.getState();
    if (findWorkToastKeyByAwaitingInputId(store.items, input.id)) continue;
    const existing = store.items[key];
    if (existing && existing.stateChangedAt >= fetchStartedAt) continue;
    store.upsertItem({
      ...existing,
      key,
      source: existing?.source ?? (input.native_subagent_id ? "native_worker" : "agent"),
      tracked: existing?.tracked ?? false,
      state: "question",
      stateBeforeBlocked: null,
      // The serializer can send "", so fall through on empty strings, not just null.
      agentName: input.name || input.agent_key || "Agent",
      projectId,
      conversationId: input.conversation_id ?? existing?.conversationId ?? null,
      taskId: input.task_id ?? existing?.taskId ?? null,
      nativeSubagentId: input.native_subagent_id ?? existing?.nativeSubagentId ?? null,
      detail: input.question,
      resultText: existing?.resultText ?? null,
      question: {
        awaitingInputId: input.id,
        question: input.question,
        context: input.context || null,
        options: input.options ?? [],
      },
      approval: null,
      stateChangedAt: now,
      dismissed: false,
    });
  }
}
